// ─────────────────────────────────────────────────────────────────────────────
// ViewSmartCooler — colour configuration
// All hex values reference APP_HEX so they stay in sync with the theme palette.
// Tailwind class strings are placed here so the page component stays clean.
// ─────────────────────────────────────────────────────────────────────────────
import { APP_HEX } from '@/constant/theme/hex.constant';
import { APP_TEXT } from '@/constant/theme/colors.constant';

export const SMART_COOLER_PROPS = {
  HEADING: 'Smart Coolers',
  SUB_HEADING: 'Monitor device connection status and system state in real time',
  LABELS: {
    COOLER_ID: 'Cooler ID',
    NATIVE_ID: 'Native ID',
    DEVICE_ID: 'Device ID',
    OPERATOR_INFO: 'Operator Info',
    MARKET_ID: 'Market ID',
    DEVICE_TYPE: 'Device Type',
    STATUS: 'Status',
    SYSTEM_STATE: 'System State',
    CREATED_ON: 'Created On',
    UPDATED_ON: 'Updated On',
    ACTIONS: 'Actions',
    UPLOAD_LOGS: 'Upload Logs',
    RESTART: 'Restart',
    OTHERS: 'Others',
    VIEW_DETAILS: 'View Details',
  },
  TOOLTIPS: {
    UPLOAD_LOGS: 'Upload device logs',
    UPLOAD_UNAVAILABLE: 'Upload unavailable — device is disconnected',
    COMING_SOON: 'Coming soon',
  },
  FILTERS: {
    STATUS: 'Select Status',
  },
};

export const SMART_COOLER_STATUS_VALUES = {
  CONNECTED: 'CONNECTED',
  DISCONNECTED: 'DISCONNECTED',
};

export const SMART_COOLER_SYSTEM_STATE_VALUES = {
  ONLINE: 'ONLINE',
  OFFLINE: 'OFFLINE',
  UNKNOWN: 'UNKNOWN',
};

export const SMART_COOLER_STATUS = [
  { id: 'ALL', name: 'All Status' },
  { id: SMART_COOLER_STATUS_VALUES.CONNECTED, name: 'Connected' },
  { id: SMART_COOLER_STATUS_VALUES.DISCONNECTED, name: 'Disconnected' },
];

export type FetchLogRange = 'TODAY' | 'LAST_2_DAYS' | 'LAST_3_DAYS';

export const FETCH_LOG_OPTIONS = [
  {
    id: 'TODAY' as FetchLogRange,
    label: 'Today',
    description: 'Logs recorded since midnight today',
  },
  {
    id: 'LAST_2_DAYS' as FetchLogRange,
    label: 'Last 2 Days',
    description: 'Today and the previous calendar day',
  },
  {
    id: 'LAST_3_DAYS' as FetchLogRange,
    label: 'Last 3 Days',
    description: 'Today and the 2 preceding calendar days',
  },
];

export const RANGE_DAYS_MAP: Record<FetchLogRange, number> = {
  TODAY: 1,
  LAST_2_DAYS: 2,
  LAST_3_DAYS: 3,
};

/** Gradient hex pairs + glow for each KPI card state */
export const SMART_COOLER_KPI_HEX = {
  connected: {
    from: APP_HEX.success,
    to: APP_HEX.emeraldSoft,
    glow: APP_HEX.success,
  },
  disconnected: {
    from: APP_HEX.danger,
    to: APP_HEX.redSoft,
    glow: APP_HEX.danger,
  },
  sysOnline: {
    from: APP_HEX.success,
    to: APP_HEX.emeraldLight,
    glow: APP_HEX.success,
  },
  sysOffline: {
    from: APP_HEX.warning,
    to: APP_HEX.yellowSoft,
    glow: APP_HEX.warning,
  },
  appVersion: {
    from: APP_HEX.indigoSoft,
    to: APP_HEX.indigoLight,
    glow: APP_HEX.indigoSoft,
  },
} as const;

/** Tailwind class strings for KPI card labels, badges, and footer chips */
export const SMART_COOLER_KPI_CLASSES = {
  connected: {
    primaryText: 'text-emerald-600 dark:text-emerald-300',
    badge:
      'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 border-emerald-300 dark:border-emerald-800/50',
    footerChip: 'text-emerald-700 dark:text-emerald-400 bg-emerald-500/15',
    footerLabel: 'Active',
  },
  disconnected: {
    primaryText: 'text-red-700 dark:text-red-300',
    badge:
      'bg-red-100 dark:bg-red-950/60 text-red-800 dark:text-red-300 border-red-300 dark:border-red-800/50',
    footerChip: 'text-red-700 dark:text-red-400 bg-red-500/15',
    footerLabel: 'Down',
  },
  sysOnline: {
    primaryText: 'text-emerald-700 dark:text-emerald-400',
    badge:
      'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 border-emerald-300 dark:border-emerald-800/50',
    footerChip: 'text-emerald-700 dark:text-emerald-400 bg-emerald-500/15',
    footerLabel: 'Healthy',
  },
  sysOffline: {
    primaryText: 'text-amber-700 dark:text-amber-400',
    badge:
      'bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 border-amber-300 dark:border-amber-800/50',
    footerChip: 'text-amber-700 dark:text-amber-400 bg-amber-500/15',
    footerLabel: 'Check',
  },
  appVersion: {
    primaryText: 'text-indigo-700 dark:text-indigo-300',
    footerChip: 'text-indigo-600 dark:text-indigo-400 bg-indigo-500/15',
  },
} as const;

/** Storage KPI card colour config — indexed by usage threshold */
export const SMART_COOLER_STORAGE_COLORS = {
  critical: {
    from: APP_HEX.danger,
    to: APP_HEX.redSoft,
    text: 'text-red-700 dark:text-red-400',
    track: 'bg-red-100 dark:bg-red-950/40',
    chip: 'text-red-700 dark:text-red-400 bg-red-500/15',
    bar: 'bg-red-500',
    label: 'Critical',
    sub: 'Critical — low space',
  },
  warning: {
    from: APP_HEX.warning,
    to: APP_HEX.yellowSoft,
    text: 'text-amber-700 dark:text-amber-400',
    track: 'bg-amber-100 dark:bg-amber-950/40',
    chip: 'text-amber-700 dark:text-amber-400 bg-amber-500/15',
    bar: 'bg-amber-500',
    label: 'Warning',
    sub: 'Moderate usage',
  },
  healthy: {
    from: APP_HEX.cyan,
    to: APP_HEX.cyanSoft,
    text: 'text-cyan-700 dark:text-cyan-400',
    track: 'bg-cyan-100 dark:bg-cyan-950/40',
    chip: 'text-cyan-700 dark:text-cyan-400 bg-cyan-500/15',
    bar: 'bg-cyan-500',
    label: 'Good',
    sub: 'Healthy',
  },
  noData: {
    from: APP_HEX.muted,
    to: APP_HEX.gray400,
    text: APP_TEXT.soft,
  },
} as const;

/** Returns the right storage colour tier given a pct value (or null for no-data) */
export const getStorageColorTier = (pct: number | null) =>
  pct === null
    ? SMART_COOLER_STORAGE_COLORS.noData
    : pct > 80
      ? SMART_COOLER_STORAGE_COLORS.critical
      : pct > 60
        ? SMART_COOLER_STORAGE_COLORS.warning
        : SMART_COOLER_STORAGE_COLORS.healthy;

/** Hex accent pairs for log-row status indicators (inline gradient/glow styles) */
export const SMART_COOLER_LOG_ACCENT = {
  failed: {
    from: APP_HEX.danger,
    to: APP_HEX.redSoft,
    border: 'border-red-200 dark:border-red-800/50',
  },
  uploading: {
    from: APP_HEX.sky,
    to: APP_HEX.skyLight,
    border: 'border-sky-200 dark:border-sky-800/50',
  },
  processed: {
    from: APP_HEX.success,
    to: APP_HEX.emeraldSoft,
    border: 'border-emerald-200 dark:border-emerald-800/50',
  },
  default: { from: APP_HEX.info, to: APP_HEX.indigoSoft, border: '' }, // falls back to APP_BORDER.soft in JSX
} as const;

/** Tailwind class strings for small status chips in the log section */
export const SMART_COOLER_LOG_CHIP = {
  uploading:
    'inline-flex items-center gap-1.5 rounded-full bg-sky-50 dark:bg-sky-950/50 border border-sky-200 dark:border-sky-800 px-2.5 py-1 text-[11px] font-semibold text-sky-600 dark:text-sky-300',
  failed:
    'inline-flex items-center gap-1.5 rounded-full bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-800 px-2.5 py-1 text-[11px] font-semibold text-red-600 dark:text-red-300',
  nativeId:
    'text-[10px] font-medium rounded-md px-1.5 py-0.5 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800/50',
  timestamp:
    'text-[10px] font-medium rounded-md px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400',
  syncing:
    'inline-flex items-center gap-1.5 text-[11px] font-medium text-indigo-500 dark:text-indigo-400',
} as const;

/** Tailwind class strings for the right-hand connection status panel */
export const SMART_COOLER_STATUS_CLASSES = {
  connected: {
    panelBorder: 'border-emerald-200 dark:border-emerald-800/60',
    heroBg: 'bg-emerald-50 dark:bg-emerald-950/30',
    glowBg: 'bg-emerald-400',
    iconRing:
      'bg-emerald-100 dark:bg-emerald-900/60 ring-emerald-200 dark:ring-emerald-800/50',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    labelText: 'text-emerald-800 dark:text-emerald-300',
    subText: 'text-emerald-700 dark:text-emerald-400/70',
    lastSeenPill:
      'bg-emerald-100/80 dark:bg-emerald-900/50 border-emerald-300/60 dark:border-emerald-700/50 text-emerald-800 dark:text-emerald-300',
    systemText: 'text-emerald-700 dark:text-emerald-400',
    stateLabel: 'Online',
    stateDesc: 'Device is reachable',
  },
  disconnected: {
    panelBorder: 'border-red-200 dark:border-red-800/60',
    heroBg: 'bg-red-50 dark:bg-red-950/25',
    glowBg: 'bg-red-400',
    iconRing: 'bg-red-100 dark:bg-red-900/60 ring-red-200 dark:ring-red-800/50',
    iconColor: 'text-red-600 dark:text-red-400',
    labelText: 'text-red-800 dark:text-red-300',
    subText: 'text-red-700 dark:text-red-400/70',
    lastSeenPill:
      'bg-red-100/80 dark:bg-red-900/50 border-red-300/60 dark:border-red-700/50 text-red-800 dark:text-red-300',
    systemText: 'text-red-700 dark:text-red-400',
    stateLabel: 'Offline',
    stateDesc: 'Device is unreachable',
  },
} as const;

/** Inline `style={{ background }}` strings for gradient washes in the log section */
export const VIEW_SC_STYLES = {
  logToolbarGradient: `linear-gradient(120deg, ${APP_HEX.info} 0%, transparent 60%)`,
  logIconBubbleBg: `linear-gradient(135deg, ${APP_HEX.info}22, ${APP_HEX.indigoSoft}33)`,
  logIconBubbleBorder: `1px solid ${APP_HEX.info}30`,
  logEmptyRadial: `radial-gradient(circle at 50% 40%, ${APP_HEX.info}, transparent 70%)`,
  logEmptyIconBg: `linear-gradient(135deg, ${APP_HEX.info}15, ${APP_HEX.indigoSoft}20)`,
  logEmptyIconBorder: `1px solid ${APP_HEX.info}25`,
  paginationGradient: `linear-gradient(120deg, ${APP_HEX.info} 0%, transparent 50%)`,
} as const;

/** Inline gradient strings used inside AppLogExpandedPanel array-item cards */
export const APP_LOG_PANEL_STYLES = {
  arrayItemTopLine: `linear-gradient(to right, ${APP_HEX.info}, ${APP_HEX.indigoSoft}, ${APP_HEX.indigoLight})`,
  arrayItemHeaderBg: `linear-gradient(to right, ${APP_HEX.info}08, transparent)`,
  arrayItemBadgeBg: `linear-gradient(135deg, ${APP_HEX.info}, ${APP_HEX.indigoSoft})`,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// ViewSmartCooler — UI string constants
// Every user-facing label, message, placeholder and button caption used in the
// device detail page. Keeps the JSX free of bare string literals.
// ─────────────────────────────────────────────────────────────────────────────
export const VIEW_SC_STRINGS = {
  // ── Page / not-found ──────────────────────────────────────────────────────
  NOT_FOUND_TITLE: 'Device not found',
  NOT_FOUND_DESC: 'This cooler may have been removed or the link is invalid.',
  BACK_BUTTON: 'Back to Smart Coolers',
  BACK_SHORT: 'Back',
  FETCH_LOGS_BUTTON: 'Upload Logs',

  // ── KPI cards ─────────────────────────────────────────────────────────────
  KPI_CONNECTION_LABEL: 'Connection',
  KPI_SYS_STATE_LABEL: 'System State',
  KPI_APP_VERSION_LABEL: 'App Version',
  KPI_STORAGE_LABEL: 'Storage',

  KPI_CONN_PRIMARY_LIVE: 'Connected',
  KPI_CONN_PRIMARY_OFFLINE: 'Disconnected',
  KPI_CONN_SUB_NEVER: 'Never connected',
  KPI_CONN_SUB_DISC_NEVER: 'Never disconnected',

  KPI_SYS_PRIMARY_ONLINE: 'Online',
  KPI_SYS_PRIMARY_OFFLINE: 'Offline',
  KPI_SYS_UNKNOWN: 'Unknown',
  KPI_SYS_SUB_HEARTBEAT: 'Heartbeat', // prepended to relative time
  KPI_SYS_SUB_NONE: 'No heartbeat',

  KPI_VERSION_OS_PREFIX: 'OS',
  KPI_VERSION_OS_FALLBACK: 'OS version unavailable',
  KPI_VERSION_ARCH_FALLBACK: 'Architecture unknown',
  KPI_VERSION_INSTALLED: 'Installed',

  KPI_STORAGE_USAGE_UNKNOWN: 'Usage unknown',
  KPI_STORAGE_NO_DATA: 'No storage data',

  // ── Overview tab ─────────────────────────────────────────────────────────
  SECTION_IDENTITY_TITLE: 'Device Identity',
  SECTION_IDENTITY_SUB: 'Core identifiers and hardware classification',
  SECTION_OPERATOR_TITLE: 'Operator & Market',
  SECTION_OPERATOR_SUB: 'Business hierarchy for this device',
  SECTION_TIMESTAMPS_TITLE: 'Record Timestamps',
  SECTION_TIMESTAMPS_SUB: 'Created and last modified',

  LABEL_COOLER_ID: 'Cooler ID',
  LABEL_NATIVE_ID: 'Native ID',
  LABEL_DEVICE_ID: 'Device ID',
  LABEL_DEVICE_SN: 'Device SN',
  LABEL_READER_SN: 'Reader SN',
  LABEL_DEVICE_TYPE: 'Device Type',
  LABEL_OPERATOR_ID: 'Operator ID',
  LABEL_PARTNER_UID: 'Partner UID',
  LABEL_MARKET_ID: 'Market ID',
  LABEL_CREATED_ON: 'Created On',
  LABEL_UPDATED_ON: 'Updated On',

  // ── System tab ────────────────────────────────────────────────────────────
  SECTION_SOFTWARE_TITLE: 'Software',
  SECTION_SOFTWARE_SUB: 'Application, OS and network details',
  SECTION_DISK_TITLE: 'Disk Storage',
  SECTION_DISK_SUB: 'Filesystem at last report',
  SECTION_POD_TITLE: 'Pod Assignment',
  SECTION_POD_SUB: 'Kubernetes pod details',

  SYS_NO_DATA_TITLE: 'No system data',
  SYS_NO_DATA_DESC: 'This device has not reported system info yet.',
  SYS_USAGE_LABEL: 'Usage',
  SYS_USED_SUFFIX: '% used',
  SYS_MB_SUFFIX: 'MB',
  SYS_STAT_USED: 'Used',
  SYS_STAT_FREE: 'Free',
  SYS_STAT_TOTAL: 'Total',

  LABEL_APP_VERSION: 'App Version',
  LABEL_OS_VERSION: 'OS Version',
  LABEL_NETWORK: 'Network',
  LABEL_TIMEZONE: 'Timezone',
  LABEL_SYSTEM_TIME: 'System Time',
  LABEL_LAST_UPDATED: 'Last Updated',
  LABEL_POD_ID: 'Pod ID',
  LABEL_INTERNAL_URL: 'Internal URL',

  // ── Connection tab ────────────────────────────────────────────────────────
  CONN_TIMELINE_TITLE: 'Connection Timeline',
  CONN_TIMELINE_SUB: 'Recent device connectivity events',
  CONN_LAST_CONNECTED: 'Last Connected',
  CONN_LAST_DISCONNECTED: 'Last Disconnected',
  CONN_LAST_HEARTBEAT: 'Last Heartbeat',
  CONN_LAST_FAILED_HB: 'Last Failed Heartbeat',

  CONN_STATS_SYSTEM: 'System',
  CONN_STATS_HEARTBEAT: 'Heartbeat',

  CONN_FETCH_TITLE: 'Upload Device Logs',
  CONN_FETCH_DESC: 'Request the device to upload logs for the last 1–3 days.',
  CONN_FETCH_BTN_ONLINE: 'Upload Logs',
  CONN_FETCH_BTN_OFFLINE: 'Device Offline',
  CONN_VIEW_LOGS_LABEL: 'Device Logs',
  CONN_VIEW_LOGS_PREFIX: 'View files in the',
  CONN_VIEW_LOGS_SUFFIX: 'tab.',

  // ── Logs tab ──────────────────────────────────────────────────────────────
  LOG_TOOLBAR_TITLE: 'Uploaded Log Files',
  LOG_FILE_SINGULAR: 'file',
  LOG_FILE_PLURAL: 'files',
  LOG_SYNCING: 'Syncing…',
  LOG_QUERIED_BY: 'Queried by',
  LOG_NATIVE_ID_BADGE: 'Native ID = Log Device ID',
  LOG_REFRESH_BTN: 'Refresh',

  LOG_EMPTY_TITLE: 'No log files yet',
  LOG_EMPTY_DESC:
    'Trigger a log upload from this device. Files appear here once processed.',
  LOG_OFFLINE_BANNER: 'Device offline — cannot upload logs',
  LOG_FETCH_NOW_BTN: 'Upload Logs Now',

  LOG_ROW_DETAILS_LABEL: 'Log Details',
  LOG_ROW_UPLOADING: 'Uploading',
  LOG_ROW_FAILED: 'Failed',
  LOG_ROW_DOWNLOADING: 'Downloading',
  LOG_ROW_DOWNLOAD_BTN: 'Download',

  LOG_PAGE_PREFIX: 'Page',
  LOG_PAGE_OF: 'of',
  LOG_PAGE_TOTAL_SUFFIX: 'total files',
  LOG_PAGE_PREV: '← Prev',
  LOG_PAGE_NEXT: 'Next →',
  LOG_PAGE_DOT_SEP: '·',

  COOLER_ACTION_SUCCESS_MSG: 'Cooler action completed successfully.'
} as const;
